import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Clock, ChevronDown, Check, X, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const DEFAULT_STEP_MINUTES = 15;

function generateTimeOptions(stepMinutes) {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += stepMinutes) {
      const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const label = `${displayHour}:${String(minute).padStart(2, '0')} ${ampm}`;
      times.push({ value, label });
    }
  }
  return times;
}

const DEFAULT_TIME_OPTIONS = generateTimeOptions(DEFAULT_STEP_MINUTES);

function formatTimeFallback(value) {
  const parts = value.split(':');
  if (parts.length !== 2) return value;
  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return value;
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const ampm = hour < 12 ? 'AM' : 'PM';
  return `${displayHour}:${String(minute).padStart(2, '0')} ${ampm}`;
}

export function TimePicker({
  id,
  label,
  value,
  onChange,
  placeholder = 'Select time...',
  step = DEFAULT_STEP_MINUTES,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  const timeOptions = useMemo(
    () => (step === DEFAULT_STEP_MINUTES ? DEFAULT_TIME_OPTIONS : generateTimeOptions(step)),
    [step]
  );

  const filteredTimes = useMemo(() => {
    if (!search) return timeOptions;
    const query = search.toLowerCase();
    return timeOptions.filter((time) => time.label.toLowerCase().includes(query));
  }, [search, timeOptions]);

  const displayLabel = useMemo(() => {
    if (!value) return '';
    const match = timeOptions.find((time) => time.value === value);
    if (match) return match.label;
    return formatTimeFallback(value);
  }, [value, timeOptions]);

  const closeDropdown = useCallback(() => { setOpen(false); setSearch(''); }, []);

  useEffect(() => { setHighlightedIndex(0); }, [search, open]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) closeDropdown();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, closeDropdown]);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(event) {
      if (filteredTimes.length === 0) {
        if (event.key === 'Escape') { event.preventDefault(); closeDropdown(); triggerRef.current?.focus(); }
        else if (event.key === 'Tab') closeDropdown();
        return;
      }
      if (event.key === 'ArrowDown') { event.preventDefault(); setHighlightedIndex((index) => (index + 1) % filteredTimes.length); }
      else if (event.key === 'ArrowUp') { event.preventDefault(); setHighlightedIndex((index) => (index <= 0 ? filteredTimes.length - 1 : index - 1)); }
      else if (event.key === 'Enter') { event.preventDefault(); if (highlightedIndex >= 0 && highlightedIndex < filteredTimes.length) { onChange(filteredTimes[highlightedIndex].value); closeDropdown(); triggerRef.current?.focus(); } }
      else if (event.key === 'Escape') { event.preventDefault(); closeDropdown(); triggerRef.current?.focus(); }
      else if (event.key === 'Tab') closeDropdown();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, highlightedIndex, filteredTimes, onChange, closeDropdown]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const options = listRef.current.querySelectorAll('[role="option"]');
    const item = options[highlightedIndex];
    if (item) item.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, open]);

  useEffect(() => {
    if (!open || !value || !listRef.current) return;
    const selectedIndex = filteredTimes.findIndex((time) => time.value === value);
    if (selectedIndex >= 0) {
      setHighlightedIndex(selectedIndex);
      requestAnimationFrame(() => {
        const options = listRef.current?.querySelectorAll('[role="option"]');
        const item = options?.[selectedIndex];
        if (item) item.scrollIntoView({ block: 'center' });
      });
    }
  }, [open]);

  function handleClear(event) {
    event.stopPropagation();
    onChange('');
  }

  return (
    <div ref={ref} className="relative w-full">
      {label && (
        <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-slate-700">
          <span className="flex items-center gap-1.5"><Clock className="size-3.5" /> {label}</span>
        </label>
      )}
      <button
        ref={triggerRef}
        id={id}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex h-12 w-full items-center justify-between gap-2 rounded-xl border bg-white px-4 text-sm shadow-sm transition-colors hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1',
          value ? 'border-brand-500 ring-1 ring-brand-200' : 'border-slate-300'
        )}
      >
        <span className="flex items-center gap-2">
          <Clock className="size-4 shrink-0 text-slate-400" />
          {value ? (
            <span className="font-medium text-slate-900">{displayLabel}</span>
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {value && (
            <span role="button" tabIndex={-1} onMouseDown={(event) => event.preventDefault()} onClick={handleClear} className="rounded-full p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
              <X className="size-3.5" />
            </span>
          )}
          <ChevronDown className={cn('size-4 text-slate-500 transition-transform', open && 'rotate-180')} />
        </span>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-40 mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
              <Input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search time..."
                className="h-10! cursor-text pl-9 text-sm"
                autoFocus
              />
            </div>
          </div>
          <div ref={listRef} role="listbox" className="max-h-56 overflow-y-auto p-1">
            {filteredTimes.length === 0 ? (
              <p className="p-4 text-center text-sm text-slate-500">No matching times</p>
            ) : (
              filteredTimes.map((time, index) => {
                const isSelected = value === time.value;
                const isHighlighted = index === highlightedIndex;
                return (
                  <button
                    key={time.value}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    aria-selected={isSelected}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => { onChange(time.value); closeDropdown(); triggerRef.current?.focus(); }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors',
                      isHighlighted && 'bg-slate-100',
                      isSelected && 'font-semibold text-brand-700'
                    )}
                  >
                    <span>{time.label}</span>
                    {isSelected && <Check className="size-4 text-brand-600" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}